import { Logger } from '@/lib/log.ts';

const log = Logger('header.cache');

/**
 *
 * @param key
 * @param factory
 * @param ttl = 7200000
 * @param cacheType = localStorage
 * @returns
 */
function getOrCreate(
	key: string,
	factory: CacheFactory,
	ttl = 7200000,
	cacheType = 'localStorage',
): Promise<CacheStorageItem> {
	if (!key) {
		return Promise.reject(new Error("Missing required parameter 'key'"));
	}

	if (typeof factory !== 'function') {
		return Promise.reject(new Error('Parameter value is not a function'));
	}

	// Parse ttl as int, in case it has been passed as string (from env vars)
	ttl = parseInt(String(ttl), 10);

	let cacheValue = null;

	try {
		cacheValue = JSON.parse(window[cacheType].getItem(key));
	} catch (e) {
		Promise.reject(new Error(`Error parsing item ${key} from ${cacheType}: ${e}`));
	}

	if (!cacheValue || Date.now() > cacheValue.expires) {
		const factoryValue = factory();

		if (!(factoryValue instanceof Promise)) {
			return Promise.reject(new Error('factory function must be a Promise.'));
		}

		return factoryValue.then((response) => {
			const now = Date.now();

			const storageItem: CacheStorageItem = {
				created: now,
				expires: now + ttl,
				value: response,
			};

			// We need to catch a possible exception for setItem, but we
			// only log a warning and still resolve the Promise with the storedValue.
			try {
				window[cacheType].setItem(key, JSON.stringify(storageItem));
			} catch (e) {
				log.warn(`Error storing item in ${cacheType}: ${e}. Item was not saved!`);
			}

			return Promise.resolve(storageItem);
		});
	} else {
		return Promise.resolve(cacheValue);
	}
}

/**
 *
 * @param key
 * @param cacheType = localStorage
 * @returns
 */
function invalidate(key: string, cacheType = 'localStorage'): Promise<void> {
	if (!key) {
		return Promise.reject(new Error("Missing required parameter 'key'"));
	}

	window[cacheType].removeItem(key);

	return Promise.resolve();
}

export { getOrCreate, invalidate };
